"use client";

import { useState, useMemo } from "react";
import { SignupForm } from "./SignupForm";
import { LoginForm } from "./LoginForm";
import { useQuiz } from "../contexts/AppContext";
import { useSearchParams } from "next/navigation";
import { getReferralData } from "../lib/referral-tracking";

interface QuizAccountStepProps {
  firstName: string;
  email: string;
  onComplete: () => void;
  isSubmitting?: boolean;
  customHeading?: string;
  customSubheading?: string;
  promoCode?: string;
}

export function QuizAccountStep({ firstName, email, onComplete, isSubmitting, customHeading, customSubheading, promoCode }: QuizAccountStepProps) {
  const [mode, setMode] = useState<'signup' | 'login'>('signup');
  const { quizAnswers } = useQuiz();
  const searchParams = useSearchParams();

  // Get referral data to pass to signup
  const referralData = getReferralData();
  
  // Build the redirect URL with the temp quiz ID
  const redirectUrl = useMemo(() => {
    // Check if we're coming from quick-quiz (therapist specific)
    const therapistSlug = searchParams.get('therapist');
    if (therapistSlug) {
      // Build the booking URL with all quiz parameters
      const params = new URLSearchParams();
      
      if (quizAnswers?.sessionType) {
        params.append('sessionType', quizAnswers.sessionType);
      }
      if (quizAnswers?.therapistType) {
        params.append('therapistType', quizAnswers.therapistType);
      }
      if (quizAnswers?.rate) {
        params.append('rate', quizAnswers.rate.toString());
      }
      if (quizAnswers?.timezone) {
        params.append('timezone', quizAnswers.timezone);
      }

      // Preserve allowBooking parameter from URL if present
      const allowBooking = searchParams.get('allowBooking');
      if (allowBooking === 'true') {
        params.append('allowBooking', 'true');
      }

      const queryString = params.toString();
      const bookingUrl = `/therapist/${therapistSlug}/book${queryString ? `?${queryString}` : ''}`;
      return bookingUrl;
    }
    
    const tempId = searchParams.get('tempId');
    if (tempId) {
      return `/quiz/processing?tempId=${tempId}`;
    }
    // Fallback - shouldn't happen
    return '/quiz';
  }, [searchParams, quizAnswers]);

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">
          {customHeading || 'One last step to see your matches'}
        </h2>
        <p className="text-lg text-gray-600">
          {customSubheading || "Create your free account to view your personalized therapist recommendations"}
        </p>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-8">
        {/* Tab switcher */}
        <div className="flex space-x-1 mb-8 bg-gray-100 p-1 rounded-lg">
          <button
            type="button"
            onClick={() => setMode('signup')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              mode === 'signup'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Create Account
          </button>
          <button
            type="button"
            onClick={() => setMode('login')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              mode === 'login'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Sign In
          </button>
        </div>

        {mode === 'signup' ? (
          <SignupForm
            onSuccess={onComplete}
            defaultValues={{
              firstName: firstName,
              email: email,
              timezone: quizAnswers?.timezone
            }}
            redirectTo={redirectUrl}
            referralData={{
              isPaidReferrer: referralData.isPaidReferrer,
              referralSource: referralData.referralSource
            }}
            promoCode={promoCode}
          />
        ) : (
          <LoginForm
            onSuccess={onComplete}
            redirectTo={redirectUrl}
            updateUserData={{
              ...(quizAnswers?.timezone && { timezone: quizAnswers.timezone })
            }}
          />
        )}
      </div>
    </div>
  );
}